/**
 * Detects the most likely UI language ("es" or "en") for an unauthenticated
 * visitor based on:
 *   1) Phone international code (LADA): e.g. +52 → México → es, +1 → US → en.
 *   2) Email TLD: e.g. user@example.mx → es, user@example.de → en (fallback).
 *   3) IP geolocation via ipapi.co (free, no key, <50ms typical).
 *   4) navigator.language (last resort).
 *
 * Returns "es" by default if everything fails (primary market).
 */
import { Country } from "country-state-city";

// Countries where the official/primary spoken language is Spanish.
const SPANISH_SPEAKING = new Set([
  "ES", "MX", "AR", "CO", "PE", "CL", "EC", "VE", "BO", "PY", "UY",
  "GT", "SV", "HN", "NI", "CR", "PA", "PR", "DO", "CU", "GQ",
]);

// Build phonecode → ISO2 once. Some phone codes are shared (US/CA = 1) — we
// take the FIRST country reported by country-state-city (US, AR, etc).
const PHONECODE_TO_ISO = (() => {
  const map = {};
  for (const c of Country.getAllCountries()) {
    const code = (c.phonecode || "").toString().replace(/[^0-9]/g, "");
    if (!code) continue;
    if (!map[code]) map[code] = c.isoCode;
  }
  return map;
})();

// Some popular TLDs map directly to a country.
const TLD_TO_ISO = {
  mx: "MX", es: "ES", ar: "AR", co: "CO", pe: "PE", cl: "CL", ec: "EC",
  ve: "VE", bo: "BO", py: "PY", uy: "UY", gt: "GT", sv: "SV", hn: "HN",
  ni: "NI", cr: "CR", pa: "PA", pr: "PR", do: "DO", cu: "CU",
  // English-leaning TLDs
  us: "US", ca: "CA", uk: "GB", au: "AU", nz: "NZ", ie: "IE",
};

function countryToLang(isoCode) {
  if (!isoCode) return null;
  return SPANISH_SPEAKING.has(isoCode.toUpperCase()) ? "es" : "en";
}

/** Returns "es" / "en" / null if the phone number does not yield a match. */
export function detectFromPhone(phone) {
  if (!phone) return null;
  // Normalise: keep digits only, no leading +.
  const digits = String(phone).replace(/[^0-9]/g, "");
  if (!digits) return null;
  // Try longest-prefix-match (up to 4 digits) — phone codes range from 1 to 4 digits.
  for (let n = 4; n >= 1; n--) {
    const prefix = digits.slice(0, n);
    const iso = PHONECODE_TO_ISO[prefix];
    if (iso) return countryToLang(iso);
  }
  return null;
}

/** Returns "es" / "en" / null based on the TLD of the email host. */
export function detectFromEmail(email) {
  if (!email || !email.includes("@")) return null;
  const host = email.split("@")[1].toLowerCase();
  // Take the last segment as the TLD (e.g. user@school.edu.mx → "mx").
  const parts = host.split(".");
  const tld = parts[parts.length - 1];
  const iso = TLD_TO_ISO[tld];
  return iso ? countryToLang(iso) : null;
}

/** Async — uses backend proxy /api/geo/lang (avoids CORS on ipapi.co). Cached in sessionStorage. */
export async function detectFromIP() {
  const cacheKey = "dyd_geo_lang";
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) return cached;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${base}/api/geo/lang`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return null;
    const data = await res.json();
    const lang = data?.lang === "es" || data?.lang === "en" ? data.lang : null;
    if (lang) sessionStorage.setItem(cacheKey, lang);
    return lang;
  } catch {
    return null;
  }
}

/** Returns "es" / "en" based on `navigator.language`. */
export function detectFromBrowser() {
  const nav = (typeof navigator !== "undefined" && navigator.language) || "";
  if (!nav) return null;
  return nav.toLowerCase().startsWith("es") ? "es" : "en";
}

/**
 * Full pipeline. Sync result if phone/email present; otherwise resolves async.
 * Always resolves to "es" or "en" (never null).
 */
export async function detectLang({ phone = "", email = "" } = {}) {
  const fromPhone = detectFromPhone(phone);
  if (fromPhone) return fromPhone;
  const fromEmail = detectFromEmail(email);
  if (fromEmail) return fromEmail;
  const fromIP = await detectFromIP();
  if (fromIP) return fromIP;
  return detectFromBrowser() || "es";
}
